// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getFocalPointStylesFromMobius from "@insite/mobius/utilities/getFocalPointStyles";
import { domToReact, HTMLReactParserOptions } from "html-react-parser";
import * as React from "react";

export const xParserOptions: HTMLReactParserOptions = {
    replace: (node: any) => {
        const { name, children, attribs = { style: "" }, ...rest } = node;
        const { style, class: className, ...otherAttribs } = attribs;

        otherAttribs.className = className;

        if (name && ["h1", "h2", "h3", "h4", "h5", "h6", "body", "p", "legend"].includes(name) && children) {
            return (
                <Typography
                    css={style as any}
                    {...otherAttribs}
                    variant={name as TypographyProps["variant"]}
                    forwardAs="p"
                >
                    {children && domToReact(children, xParserOptions)}
                </Typography>
            );
        }
        if (name === "ul") {
            return (
                <UnorderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </UnorderedList>
            );
        }
        if (name === "ol") {
            return (
                <OrderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </OrderedList>
            );
        }
        if (name === "li") {
            return (
                <ListItem css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </ListItem>
            );
        }
        if (name === "img") {
            const { alt, ...remainingAttribs } = otherAttribs;
            return <LazyImage as="span" altText={alt} css={style as any} {...remainingAttribs} />;
        }
        if (name === "a") {
            return (
                <Link css={style as any} id={otherAttribs.name} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions)}
                </Link>
            );
        }
    },
};

export function getFocalPointStyles(
    focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight",
) {
    return getFocalPointStylesFromMobius(focalPoint);
}
